import {
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  Label,
  PageHeader,
  Select,
  SkeletonRows,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
  toast,
} from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import type {
  ApprovalStatus,
  Role,
  UserCreateInput,
  UserSummary,
  UserUpdateInput,
} from '@bia-auction/shared-schema';
import {
  companySchema,
  membershipCatalogSchema,
  ROLES,
  userSummarySchema,
} from '@bia-auction/shared-schema';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Search, Users } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { CompanyPicker } from '../components/CompanyPicker';
import { ErrorMsg } from '../components/Feedback';
import { api, ApiError } from '../lib/api';
import { formatCount } from '../lib/format';
import { roleLabel } from '../lib/labels';

/** The mutable fields the form collects (a superset of create/update DTOs). */
interface UserFormValues {
  email: string;
  password: string;
  nameFirst: string;
  nameLast: string;
  roles: Role[];
  relatedCompany: string[];
}

/** §6: the flat 2-col checkbox grid becomes three labeled groups (staff / firm / participant). */
const ROLE_GROUPS: Array<{ label: () => string; roles: Role[] }> = [
  {
    label: () => t('users.roleGroup.staff'),
    roles: ['ADMIN', 'DEVELOPER', 'EDITOR', 'LOGISTIC_EDITOR', 'WEB_EDITOR'],
  },
  {
    label: () => t('users.roleGroup.firm'),
    roles: ['FIRM', 'FIRM_AUCTION', 'FIRM_USER', 'FIRM_VENDOR'],
  },
  {
    label: () => t('users.roleGroup.participant'),
    roles: ['GOLDBIDDER', 'BIDDER', 'USER', 'ANONYMOUS', 'CLIENT'],
  },
];
// Compile-time drift guard: every ROLES entry must appear in exactly one group.
const GROUPED = ROLE_GROUPS.flatMap((g) => g.roles);
if (GROUPED.length !== ROLES.length || !ROLES.every((r) => GROUPED.includes(r))) {
  throw new Error('ROLE_GROUPS drifted from shared-schema ROLES');
}

/**
 * Sentinel for the "has none of these" option in the firm/membership filters. Not a valid
 * company id (UUID) nor a valid membership id (lowercase slug, `membershipPackageSchema`),
 * so it can never collide with a real value.
 */
const NONE = '__none__';

/**
 * One labeled control in the filter bar, for the two axes whose options come from a directory
 * the page has to fetch. A failed fetch must not degrade to an empty option list: that reads
 * as "no firms exist" / "no membership tiers exist" and quietly costs the operator the axis,
 * so the slot shows the error (with its requestId) in the control's place instead.
 */
function FilterField({
  htmlFor,
  label,
  error,
  children,
}: {
  htmlFor: string;
  label: string;
  error?: unknown;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      {/* Nothing to point at while the control is replaced by the error. */}
      <Label htmlFor={error ? undefined : htmlFor}>{label}</Label>
      {error ? <ErrorMsg error={error} /> : children}
    </div>
  );
}

function RolePicker({ value, onChange }: { value: Role[]; onChange: (r: Role[]) => void }) {
  const toggle = (r: Role) =>
    onChange(value.includes(r) ? value.filter((x) => x !== r) : [...value, r]);
  return (
    <div className="max-h-56 space-y-2 overflow-y-auto rounded-md border p-2">
      {ROLE_GROUPS.map((g) => (
        <fieldset key={g.label()}>
          <legend className="mb-1 text-xs font-medium text-muted-foreground">{g.label()}</legend>
          <div className="grid grid-cols-2 gap-1">
            {g.roles.map((r) => (
              <label key={r} className="flex items-center gap-2 text-sm">
                <Checkbox checked={value.includes(r)} onCheckedChange={() => toggle(r)} />
                {roleLabel(r)}
              </label>
            ))}
          </div>
        </fieldset>
      ))}
    </div>
  );
}

function UserForm({
  initial,
  onSubmit,
  submitting,
}: {
  initial?: UserSummary;
  onSubmit: (data: UserFormValues) => void;
  submitting: boolean;
}) {
  const [email, setEmail] = useState(initial?.email ?? '');
  const [password, setPassword] = useState('');
  const [nameFirst, setNameFirst] = useState(initial?.nameFirst ?? '');
  const [nameLast, setNameLast] = useState(initial?.nameLast ?? '');
  const [roles, setRoles] = useState<Role[]>(initial?.roles ?? []);
  const [companies, setCompanies] = useState<string[]>(initial?.relatedCompany ?? []);

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ email, password, nameFirst, nameLast, roles, relatedCompany: companies });
      }}
    >
      {!initial && (
        <div className="space-y-1.5">
          <Label htmlFor="uf-email">{t('login.email')}</Label>
          <Input id="uf-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1.5">
          <Label htmlFor="uf-first">{t('users.field.first')}</Label>
          <Input id="uf-first" value={nameFirst} onChange={(e) => setNameFirst(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="uf-last">{t('users.field.last')}</Label>
          <Input id="uf-last" value={nameLast} onChange={(e) => setNameLast(e.target.value)} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="uf-password">{initial ? t('users.field.newPassword') : t('users.field.password')}</Label>
        <Input
          id="uf-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={8}
          required={!initial}
        />
      </div>
      <div className="space-y-1.5">
        <Label>{t('users.field.roles')}</Label>
        <RolePicker value={roles} onChange={setRoles} />
      </div>
      <div className="space-y-1.5">
        <Label>{t('users.field.companies')}</Label>
        <CompanyPicker value={companies} onChange={setCompanies} />
      </div>
      <DialogFooter>
        <Button type="submit" disabled={submitting}>
          {t('common.save')}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function UsersPage() {
  const qc = useQueryClient();
  // One dialog shell for create + edit (redesign-admin.md §6): null = closed,
  // `{}` = create, `{ user }` = edit.
  const [form, setForm] = useState<{ user?: UserSummary } | null>(null);
  const [toDelete, setToDelete] = useState<UserSummary | null>(null);
  const [search, setSearch] = useState('');
  // The approval queue (M15). Pending opens first: it is the tab with work in it, and an
  // application nobody looks at is a bidder who can never sign in.
  const [tab, setTab] = useState<ApprovalStatus>('pending');
  const [toReject, setToReject] = useState<UserSummary | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  // Every close goes through here. Closing a *controlled* dialog by flipping `open` does not
  // fire `onOpenChange`, so a path that only nulled the subject left the typed reason behind —
  // and the next account rejected would have been recorded with someone else's rationale.
  const closeReject = () => {
    setToReject(null);
    setRejectReason('');
  };
  // Filters (role / firm / membership). Like the search box these are client-side: the list
  // route is unpaginated, so the whole directory is already in memory. Status is the exception
  // — it is a server filter, because the queue must stay small however many accounts pile up.
  const [roleFilter, setRoleFilter] = useState<Role | ''>('');
  const [firmFilter, setFirmFilter] = useState('');
  // Holds the granted role, not a tier id — see `membershipOptions` below.
  const [membershipFilter, setMembershipFilter] = useState<Role | typeof NONE | ''>('');

  const users = useQuery({
    queryKey: ['users', tab],
    queryFn: () => api(`/admin/users?status=${tab}`, {}, z.array(userSummarySchema)),
  });

  // The filter reads the INCLUSIVE directory, unlike `CompanyPicker`. Hiding an archived firm
  // from the picker is the point (a retired partner must not be newly assignable), but users
  // keep its id in `relatedCompany` — drop it here and they are reachable by no firm value at
  // all, and they are not "Firmasız" either. Key shape matches `FirmsPage`'s archived view, so
  // the two share one cached fetch.
  const companies = useQuery({
    queryKey: ['companies', '', true],
    queryFn: () => api('/admin/companies?includeArchived=1', {}, z.array(companySchema)),
    staleTime: 5 * 60_000,
  });

  // A membership is not a column on the user — it is the role a purchased tier grants
  // (M4: `payments.grantedRole`, catalog in KV via `/admin/config/memberships`), so the filter
  // resolves the tier to its role. Inactive tiers stay listed: whoever bought one still holds
  // the role, and would otherwise be unfindable.
  const memberships = useQuery({
    queryKey: ['config', 'memberships'],
    queryFn: () => api('/admin/config/memberships', {}, membershipCatalogSchema),
    staleTime: 5 * 60_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['users'] });
  const fail = (e: unknown) => toast(e instanceof ApiError ? e.message : t('common.error'), 'error');

  const create = useMutation({
    mutationFn: (body: UserCreateInput) =>
      api<UserSummary>('/admin/users', { method: 'POST', body }),
    onSuccess: () => {
      toast(t('users.created'), 'success');
      setForm(null);
      void invalidate();
    },
    onError: fail,
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserUpdateInput }) =>
      api<UserSummary>(`/admin/users/${id}`, { method: 'PATCH', body: data }),
    onSuccess: () => {
      toast(t('users.updated'), 'success');
      setForm(null);
      void invalidate();
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api<void>(`/admin/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast(t('users.deleted'), 'success');
      setToDelete(null);
      void invalidate();
    },
    onError: (e) => {
      setToDelete(null);
      fail(e);
    },
  });

  const approve = useMutation({
    mutationFn: (id: string) =>
      api<UserSummary>(`/admin/users/${id}/approve`, { method: 'POST' }),
    onSuccess: () => {
      toast(t('users.approved'), 'success');
      void invalidate();
    },
    onError: fail,
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api<UserSummary>(`/admin/users/${id}/reject`, {
        method: 'POST',
        body: reason ? { reason } : {},
      }),
    onSuccess: () => {
      toast(t('users.rejected'), 'success');
      closeReject();
      void invalidate();
    },
    onError: (e) => {
      closeReject();
      fail(e);
    },
  });

  const onCreate = (v: UserFormValues) =>
    create.mutate({
      email: v.email,
      password: v.password,
      nameFirst: v.nameFirst,
      nameLast: v.nameLast,
      roles: v.roles,
      relatedCompany: v.relatedCompany,
    });

  const onUpdate = (id: string, v: UserFormValues) => {
    // Send the name fields verbatim (including "") so clearing a name actually clears it — a
    // `|| undefined` would drop the field and the API would keep the old value.
    const data: UserUpdateInput = {
      nameFirst: v.nameFirst,
      nameLast: v.nameLast,
      roles: v.roles,
      relatedCompany: v.relatedCompany,
      ...(v.password ? { password: v.password } : {}),
    };
    update.mutate({ id, data });
  };

  // Client-side search (§6, OQ-2: no server pagination yet) over email + name.
  const filter = search.trim().toLocaleLowerCase('tr');
  const catalog = memberships.data ?? [];
  const membershipRoles = new Set(catalog.map((p) => p.role));
  // The catalog only guarantees unique ids, so two tiers may grant the same role — and then
  // they select exactly the same users. Collapse them into one option labelled with both
  // names: offering two identically-behaving picks would imply a distinction the data cannot
  // make. This is why the filter's value is the granted role, not the tier id.
  const membershipOptions = catalog.reduce<Array<{ role: Role; names: string[] }>>((acc, p) => {
    const seen = acc.find((o) => o.role === p.role);
    if (seen) seen.names.push(p.name);
    else acc.push({ role: p.role, names: [p.name] });
    return acc;
  }, []);
  const visible = (users.data ?? []).filter((u) => {
    if (
      filter &&
      !u.email.toLocaleLowerCase('tr').includes(filter) &&
      ![u.nameFirst, u.nameLast]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase('tr')
        .includes(filter)
    ) {
      return false;
    }
    if (roleFilter && !u.roles.includes(roleFilter)) return false;
    if (firmFilter === NONE) {
      if (u.relatedCompany.length > 0) return false;
    } else if (firmFilter && !u.relatedCompany.includes(firmFilter)) {
      return false;
    }
    if (membershipFilter === NONE) {
      if (u.roles.some((r) => membershipRoles.has(r))) return false;
    } else if (membershipFilter && !u.roles.includes(membershipFilter)) {
      return false;
    }
    return true;
  });

  const byName = [...(companies.data ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  const activeFirms = byName.filter((c) => c.archivedAt === null);
  const archivedFirms = byName.filter((c) => c.archivedAt !== null);

  const filtered = Boolean(filter || roleFilter || firmFilter || membershipFilter);
  const clearFilters = () => {
    setSearch('');
    setRoleFilter('');
    setFirmFilter('');
    setMembershipFilter('');
  };

  return (
    <div className="space-y-4">
      <PageHeader title={t('users.title')}>
        <Button size="sm" onClick={() => setForm({})}>
          {t('users.new')}
        </Button>
      </PageHeader>

      {/* Status is a **server** filter (`?status=`), unlike the three below it: the queue must
          stay small however many accounts pile up, and it is a different kind of choice — which
          set of accounts you are working, not how you narrow the set you are already in. */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as ApprovalStatus)}>
        <TabsList>
          <TabsTrigger value="pending">{t('users.tab.pending')}</TabsTrigger>
          <TabsTrigger value="approved">{t('users.tab.approved')}</TabsTrigger>
          <TabsTrigger value="rejected">{t('users.tab.rejected')}</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filter bar: free-text search plus the three axes an operator actually looks a user
          up by — role, firm, membership tier. All client-side over the loaded directory. */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="uflt-search">{t('users.filter.search')}</Label>
          <div className="relative w-64">
            <Search aria-hidden className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              id="uflt-search"
              className="pl-8"
              placeholder={t('users.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="uflt-role">{t('users.filter.role')}</Label>
          <Select
            id="uflt-role"
            className="w-48"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as Role | '')}
          >
            <option value="">{t('users.filter.roleAll')}</option>
            {ROLE_GROUPS.map((g) => (
              <optgroup key={g.label()} label={g.label()}>
                {g.roles.map((r) => (
                  <option key={r} value={r}>
                    {roleLabel(r)}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        </div>

        <FilterField htmlFor="uflt-firm" label={t('users.filter.firm')} error={companies.error}>
          <Select
            id="uflt-firm"
            className="w-48"
            value={firmFilter}
            onChange={(e) => setFirmFilter(e.target.value)}
            disabled={companies.isLoading}
          >
            <option value="">{t('users.filter.firmAll')}</option>
            <option value={NONE}>{t('users.filter.firmNone')}</option>
            {activeFirms.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
            {/* Archived firms stay selectable — their people are still in the directory — but
                under their own heading, so the state reads as a state and not as a live firm. */}
            {archivedFirms.length > 0 && (
              <optgroup label={t('firms.archivedBadge')}>
                {archivedFirms.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </optgroup>
            )}
          </Select>
        </FilterField>

        {/* Hidden only when the catalog is genuinely empty — nothing to pick. A *failed* catalog
            fetch is not an empty one, and `FilterField` says so rather than dropping the axis. */}
        {(catalog.length > 0 || memberships.isError) && (
          <FilterField
            htmlFor="uflt-membership"
            label={t('users.filter.membership')}
            error={memberships.error}
          >
            <Select
              id="uflt-membership"
              className="w-48"
              value={membershipFilter}
              onChange={(e) => setMembershipFilter(e.target.value as Role | typeof NONE | '')}
            >
              <option value="">{t('users.filter.membershipAll')}</option>
              <option value={NONE}>{t('users.filter.membershipNone')}</option>
              {membershipOptions.map((o) => (
                <option key={o.role} value={o.role}>
                  {o.names.join(' · ')}
                </option>
              ))}
            </Select>
          </FilterField>
        )}

        {filtered && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            {t('users.filter.clear')}
          </Button>
        )}

        {users.data && (
          <span className="ml-auto pb-2 text-sm text-muted-foreground tabular-nums">
            {t('users.count', { n: formatCount(visible.length) })}
          </span>
        )}
      </div>

      {users.isLoading ? (
        <SkeletonRows rows={6} />
      ) : users.isError ? (
        <ErrorMsg error={users.error} />
      ) : visible.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('users.col.email')}</TableHead>
              <TableHead>{t('users.col.name')}</TableHead>
              <TableHead>{t('users.col.roles')}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((u) => (
              <TableRow key={u.id}>
                <TableCell>
                  {/* Bidder profile (M10): cross-sale bid history + deposits + refund. */}
                  <Link
                    to="/users/$userId"
                    params={{ userId: u.id }}
                    className="text-tx-brand hover:underline"
                  >
                    {u.email}
                  </Link>
                  {/* The reason is written to justify a decision to whoever reads it next —
                      usually someone deciding whether to reinstate. Keeping it in the dialog
                      that captured it would mean it existed only for the operator who already
                      knew it. */}
                  {u.approvalStatus === 'rejected' && u.approvalRejectReason && (
                    <p className="text-xs text-muted-foreground">
                      {t('users.rejectReason')}: {u.approvalRejectReason}
                    </p>
                  )}
                </TableCell>
                <TableCell>
                  {[u.nameFirst, u.nameLast].filter(Boolean).join(' ') || t('common.none')}
                </TableCell>
                <TableCell className="space-x-1">
                  {u.roles.map((r) => (
                    <Badge key={r} variant="secondary">
                      {roleLabel(r)}
                    </Badge>
                  ))}
                </TableCell>
                <TableCell className="space-x-1 text-right">
                  {/* Offer every transition the server accepts, and only those: a decision is
                      never the last word. Revoking an account that turned out badly and
                      reinstating one refused in error are exactly the cases an operator reaches
                      this page for — the handbook tells them to do both from here. Only the
                      no-op is hidden, since the server 409s a repeat of the same decision. */}
                  {u.approvalStatus !== 'approved' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={approve.isPending}
                      onClick={() => approve.mutate(u.id)}
                    >
                      {t('users.approve')}
                    </Button>
                  )}
                  {u.approvalStatus !== 'rejected' && (
                    <Button variant="ghost" size="sm" onClick={() => setToReject(u)}>
                      {t('users.reject')}
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => setForm({ user: u })}>
                    {t('common.edit')}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setToDelete(u)}>
                    {t('common.delete')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <EmptyState
          icon={Users}
          // "No match" only when there was something to not match: an empty directory reads as
          // empty even if the operator had already typed into a filter. On the queue that empty
          // state has its own wording — nothing waiting is good news, not a missing record.
          title={
            filtered && (users.data ?? []).length > 0
              ? t('users.noMatch')
              : tab === 'pending'
                ? t('users.emptyPending')
                : t('users.empty')
          }
        />
      )}

      <Dialog open={form !== null} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{form?.user ? t('users.edit') : t('users.new')}</DialogTitle>
          </DialogHeader>
          {form && (
            <UserForm
              // Remount per subject so a previous edit's fields never leak into create.
              key={form.user?.id ?? 'create'}
              initial={form.user}
              submitting={create.isPending || update.isPending}
              onSubmit={(v) => (form.user ? onUpdate(form.user.id, v) : onCreate(v))}
            />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(o) => !o && setToDelete(null)}
        title={t('common.delete')}
        description={toDelete ? t('users.deleteConfirm', { email: toDelete.email }) : ''}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        destructive
        busy={remove.isPending}
        onConfirm={() => toDelete && remove.mutate(toDelete.id)}
      />

      {/* Reject takes an optional reason, so it needs a field rather than a bare confirm. The
          warning says the sessions go, because that is the part an operator can't see. */}
      <Dialog
        open={toReject !== null}
        onOpenChange={(o) => !o && closeReject()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('users.reject')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('users.rejectConfirm')}</p>
            <div className="space-y-1">
              <Label htmlFor="reject-reason">{t('users.rejectReason')}</Label>
              <Input
                id="reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={closeReject}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={reject.isPending}
                onClick={() =>
                  toReject && reject.mutate({ id: toReject.id, reason: rejectReason.trim() })
                }
              >
                {t('users.reject')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
